import React, { useState } from "react";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/utils/constants";

const STORAGE_KEY = "rota_view_token";
const TRUST_DAYS = 90;

export const VIEW_PASSWORD_VALUE = "__view_auth__";

function generateToken(password) {
  return btoa(`rota_auth_${password}_${Date.now()}_${Math.random().toString(36).slice(2)}`);
}

export function getStoredPassword() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const { expiry, pwd } = JSON.parse(stored);
      if (expiry && Date.now() < expiry) return pwd;
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('localStorage parse error:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
  }
  const session = sessionStorage.getItem(STORAGE_KEY);
  if (session) {
    try {
      return JSON.parse(session).pwd;
    } catch (e) {
      console.error('sessionStorage parse error:', e);
      return null;
    }
  }
  return null;
}

export function isViewAuthenticated() {
  return !!getStoredPassword();
}

export const ViewGate = ({ onAuthenticated }) => {
  const [password, setPassword] = useState("");
  const [trust, setTrust] = useState(true);
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [checking, setChecking] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      const resp = await axios.post(`${API}/verify-view`, { password });
      if (resp.data.success) {
        if (trust) {
          const expiry = Date.now() + TRUST_DAYS * 24 * 60 * 60 * 1000;
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ pwd: password, expiry }));
        } else {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ pwd: password }));
        }
        onAuthenticated(password);
      } else {
        setError(true);
        setShaking(true);
        setTimeout(() => setShaking(false), 500);
      }
    } catch (e) {
      console.error('View authentication error:', e);
      setError(true);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    }
    setChecking(false);
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="login-bg absolute inset-0 opacity-[0.07]" />
      <div className={`w-full max-w-sm relative z-10 ${shaking ? "animate-shake" : ""}`}>
        <div className="bg-white rounded-xl shadow-lg border border-[#e5e7eb] p-8">
          <div className="flex flex-col items-center mb-6">
            <div className="h-12 w-12 rounded-full bg-[#605e5e] flex items-center justify-center mb-3 overflow-hidden">
              <img src="/wve-logo.png" alt="WVE" className="h-10 w-10 object-contain" />
            </div>
            <h1 className="font-heading text-xl text-[#2d2d2d]">WVE Team Rota</h1>
            <p className="text-sm text-[#8d8b8b] mt-1">Enter password to view</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(false); }}
                  placeholder="Password"
                  className={`h-11 pr-10 ${error ? "border-red-400 ring-1 ring-red-300" : ""}`}
                  autoFocus
                  data-testid="view-gate-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8d8b8b] hover:text-[#605e5e] transition-colors"
                  tabIndex={-1}
                  data-testid="view-gate-toggle-pwd"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {error && (
                <p className="text-xs text-red-500 mt-1.5">Incorrect password</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="trust"
                checked={trust}
                onCheckedChange={setTrust}
                data-testid="view-gate-trust"
              />
              <label htmlFor="trust" className="text-sm text-[#605e5e] cursor-pointer select-none">
                Trust this browser
              </label>
              <span className="text-xs text-[#8d8b8b]">({TRUST_DAYS} days)</span>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#605e5e] hover:bg-[#4a4848] text-white"
              data-testid="view-gate-submit"
              disabled={checking}
            >
              {checking ? "Checking..." : "View Rota"}
            </Button>
          </form>
        </div>

        <p className="text-[11px] text-[#999] text-center mt-4 leading-relaxed max-w-sm px-2">
          This system is confidential and protected under NDA. It contains proprietary information belonging to Wedding Video Essex and is intended solely for authorised employees. Unauthorised access, disclosure, or distribution is strictly prohibited and may result in legal action.
        </p>
      </div>
    </div>
  );
};
